import { useEffect } from 'react';
import { useDataModel } from '@thd-nucleus/data-sources';
import { processApplianceFulfillmentResponse } from '@thd-olt-component-react/check-availability';
import { getUrlForApplianceData } from '../components/helper/utils';
import { setErrorEvents } from '../analytics';

export const useFulfillmentGetData = ({
  itemId,
  storeId,
  zipCode,
  directData = null,
  quantity = 1
}) => {
  const productOptions = {
    variables: {
      itemId,
      storeId,
      zipCode,
      quantity
    },
    ssr: false,
    skip: !!directData
  };
  const { data, loading, error } = useDataModel('clientOnlyProduct', productOptions);

  return { data: data || { product: directData }, loading, error };
};

export const useShippingGetData = ({
  itemId,
  storeId,
  zipCode,
  quantity,
  value,
  isSthAvailable,
  directData = null,
  isMajorAppliance = false
}) => {
  const shippingQueryProps = {
    variables: {
      itemId,
      storeId,
      zipCode,
      quantity,
      price: parseInt((value * quantity).toFixed(2), 10)
    },
    skip: !!directData || !isSthAvailable || isMajorAppliance // don't call shipping GraphQL for these scenarios
  };
  const { data, loading, error } = useDataModel('shipping', shippingQueryProps);

  return { shippingData: data?.shipping, shippingLoading: loading, shippingError: error };
};

export const useApplianceGetData = ({
  itemId,
  itemIds = '',
  zipCode,
  isMajorAppliance = false,
  applianceDirectData = null,
  setApplianceDeliveryData
}) => {
  // For setting direct appliance delivery data (for mocking and mycart)
  useEffect(() => {
    let data = null;
    if (!isMajorAppliance || applianceDirectData === null) return;
    if (applianceDirectData) {
      if (applianceDirectData?.DeliveryAvailabilityResponse?.errorData) {
        data = applianceDirectData?.DeliveryAvailabilityResponse?.errorData || null;

        if (data?.errors?.error) {
          const error = data?.errors?.error;
          const errorModel = {
            message: error.description || ''
          };

          if (error.errorCode) {
            errorModel.code = error.errorCode;
          }

          setErrorEvents(errorModel);
        }

      } else {
        data = applianceDirectData?.DeliveryAvailabilityResponse?.deliveryAvailability || null;
      }
      setApplianceDeliveryData(data);
    }
  }, [applianceDirectData, isMajorAppliance]);

  useEffect(() => {
    if (!isMajorAppliance || applianceDirectData) return;

    async function getDeliveryAvailability() {
      try {
        const url = getUrlForApplianceData(itemId, itemIds, zipCode);
        if (!url) {
          setApplianceDeliveryData({ errors: 'URL didn\'t build correctly.' });
        }
        const fetchResponse = await fetch(url);
        const responseJson = await fetchResponse.json();

        if (!fetchResponse.ok) {
          const error = (responseJson && responseJson.message) || fetchResponse.statusText;
          throw error;
        }

        let applianceDeliveryData = null;

        if (responseJson?.DeliveryAvailabilityResponse?.errorData) {
          applianceDeliveryData = responseJson?.DeliveryAvailabilityResponse?.errorData;
        } else {
          applianceDeliveryData = responseJson?.DeliveryAvailabilityResponse?.deliveryAvailability;
          const processedData = processApplianceFulfillmentResponse(responseJson);
          const eventData = {
            processedData,
            zipCode: processedData.zipCode || zipCode,
            autoCheck: !!applianceDeliveryData && !applianceDirectData,
            stringifiedQueryId: itemIds?.join(',') || itemId || '',
          };
          LIFE_CYCLE_EVENT_BUS.trigger('check-availability.depot-direct-success', eventData);
          LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('check-availability.depot-direct-success', eventData);
        }

        setApplianceDeliveryData(applianceDeliveryData);

      } catch (err) {
        const errorMsg = 'Fulfillment component MCC-Cart DeliveryAvailability network call had an error.';
        console.error(errorMsg, err);
        setApplianceDeliveryData({ errors: errorMsg });
      }
    }

    getDeliveryAvailability();

  }, [itemId, JSON.stringify(itemIds), zipCode, isMajorAppliance]);
};
